import React, { useState } from 'react'
import styled from '@emotion/styled'
import PropTypes from 'prop-types'
import * as yup from 'yup'
import { useForm, FormProvider } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { LinearProgress } from '@mui/material'

import UploadFileIcon from '@mui/icons-material/UploadFile'

import { categoryKey, separateCategoryStatusesAllowedEditing, separateCategoryStatusesLoadingWillAllowEditing } from '../../util/categoryConsts'
import {
  FlexRow,
  FlexEndContainer,
  PaddedForm,
  StyledDialog,
  StyledDialogContent,
  FlexColumn,
  WhiteBGContainer,
  LightBGContainer
} from '../styles/Containers'
import { ProgressBarWrapper } from '../styles/ProgressBar'
import { ErrorMessage, SuccessMessage, WarningMessage } from '../common/UserMessage'
import Button, { SecondaryButton } from '../common/Button'
import BodyText, { BoldFormTitle, BoldSpan, ErrorText, MedBodyText, SmallBodyText } from '../styles/Text'
import axios from '../../dataClient/dataClient'
import UploadFile from '../files/UploadFile'
import BrowserFile from '../files/BrowserFile'
import FailedBrowserUploadList from '../files/FailedBrowserUploadList'
import AutocompleteSelect from '../inputFields/AutocompleteSelect'

// Default destination when the user doesn't pick a category. An existing
// category with this name on the FOI (any casing) is reused; otherwise it
// rides the create-on-the-fly path.
export const UNCATEGORIZED_CATEGORY_NAME = 'Uncategorized'

export const categoryUploadFilesButtonStrings = {
  title: 'Upload Files'
}

const UploadButton = styled(Button)`
  flex-direction: column;
  gap: 10px;
  max-width: 110px;
  min-width: 110px;
  padding: 10px 20px;

  font-size: 18px;
  line-height: normal;
  font-weight: 600;

  .MuiSvgIcon-root {
    height: 47px;
    width: 47px;
  }
`

const LoadingText = styled(SmallBodyText)`
  text-wrap: nowrap;
`

const LoadingContainer = styled(FlexRow)`
  align-content: center;
`

const schema = yup.object().shape({
  category: yup
    .object()
    .shape({
      id: yup.string(),
      label: yup.string().required('Label is required')
    })
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .nullable()
    .notRequired(),
  files: yup.array().min(1, 'At least one file is required').required('At least one file is required')
})

function CategoryUploadFilesButton({ categories = [], foiId, disabled = false, onSuccess = () => {} }) {
  const [isVisible, setIsVisible] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(false)
  const [display, setDisplay] = useState('form')

  const [currUploadNumber, setCurrUploadNumber] = useState(0)
  const [errorFiles, setErrorFiles] = useState([])
  const [numUploaded, setNumUploaded] = useState()
  const [uploadedCategory, setUploadedCategory] = useState()

  const methods = useForm({ resolver: yupResolver(schema) })
  const { handleSubmit, formState, reset, watch, setValue } = methods

  const onError = (errors, e) => console.error('Error', errors, e)

  const handleOpen = () => setIsVisible(true)
  const handleClose = (_event, reason) => {
    if (['backdropClick', 'escapeKeyDown'].includes(reason)) {
      return
    }

    setIsVisible(false)
  }

  const resetDialog = () => {
    setCurrUploadNumber(0)
    setIsLoading(false)
    setError(false)
    reset()
    setDisplay('form')
  }

  async function onSubmit(data) {
    setIsLoading(true)
    setError(false)

    const filesToUpload = data.files
    const failed = []
    let numSuccess = 0
    let categoryId
    const category = data.category ??
      categories.find(c => c.label.toLowerCase() === UNCATEGORIZED_CATEGORY_NAME.toLowerCase()) ?? {
        label: UNCATEGORIZED_CATEGORY_NAME
      }

    if (!category.id) {
      try {
        const { data: responseData } = await axios.post('/category', { name: category.label, foiId })
        categoryId = responseData.data.id
      } catch (e) {
        console.error(`failed to create category with name `, category.label, e)
        setError(true)
        setIsLoading(false)
        return
      }
    } else {
      categoryId = category.id
    }

    for (const file of filesToUpload) {
      setCurrUploadNumber(currUploadNumber => currUploadNumber + 1)
      try {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('associationId', categoryId)
        formData.append('associationType', 'CATEGORY')
        formData.append('usage', 'CATEGORY')
        const config = { headers: { 'Content-Type': 'multipart/form-data' } }
        await axios.post(`/file`, formData, config)

        numSuccess++
      } catch (error) {
        failed.push(file)
        console.error(`failed to upload the file %s for category %s`, file.name, categoryId, error)
      }
    }

    setIsLoading(false)
    setErrorFiles(failed)
    setNumUploaded(numSuccess)
    setUploadedCategory(category.label)
    setDisplay('success')
    onSuccess(categoryId)
  }

  const files = watch('files')

  function handleUploadFiles(acceptedFiles) {
    const startingFiles = files || []
    setValue('files', startingFiles.concat(acceptedFiles))
  }

  function handleDelete(file) {
    const filteredFiles = files.filter(f => f !== file)
    setValue('files', filteredFiles)
  }

  const formError = !!formState.errors.files
  const helperText = formState.errors?.files?.message
  const restorableCategories = separateCategoryStatusesAllowedEditing.map(s => categoryKey[s].label).join(', ')
  const loadingCategories = separateCategoryStatusesLoadingWillAllowEditing.map(s => categoryKey[s].label).join(', ')

  return (
    <>
      <UploadButton onClick={handleOpen} disabled={disabled}>
        <UploadFileIcon />
        Upload Files
      </UploadButton>
      <StyledDialog
        maxwidth="800px"
        fullWidth
        open={isVisible}
        onClose={handleClose}
        keepMounted={false}
        disableEscapeKeyDown={true}
        slotProps={{ transition: { onExited: resetDialog } }}>
        <StyledDialogContent>
          {display === 'form' && (
            <FormProvider {...methods}>
              <PaddedForm onSubmit={handleSubmit(onSubmit, onError)}>
                <BoldFormTitle>{categoryUploadFilesButtonStrings.title}</BoldFormTitle>
                <BodyText>Uploaded files will be added to the selected category.</BodyText>
                <WarningMessage>
                  <MedBodyText>
                    Files can only be restored to a category with one of the following statuses: <BoldSpan>{restorableCategories}</BoldSpan>. If the
                    category currently has one of the following loading statuses: <BoldSpan>{loadingCategories}</BoldSpan>, you can restore the file
                    to it once it has finished processing.
                  </MedBodyText>
                </WarningMessage>
                <AutocompleteSelect id="category" label="Category" options={categories} disabled={isLoading} />
                {!isLoading && (
                  <FlexColumn gap="5px">
                    <UploadFile onUpload={handleUploadFiles} />
                    {formError && <ErrorText role="alert">{helperText}</ErrorText>}
                  </FlexColumn>
                )}
                {files && files.length > 0 && (
                  <FlexColumn gap="12px">
                    <BodyText>The below file(s) will be uploaded to the category.</BodyText>
                    <LightBGContainer padding="5px 20px 5px 5px">
                      {files.map((f, i) => {
                        return <BrowserFile key={i} file={f} onDelete={() => handleDelete(f)} loading={isLoading} />
                      })}
                    </LightBGContainer>
                  </FlexColumn>
                )}
                {isLoading && (
                  <LoadingContainer>
                    <ProgressBarWrapper>
                      <LinearProgress variant="determinate" value={(currUploadNumber / files.length) * 100} />
                    </ProgressBarWrapper>
                    <LoadingText>
                      Uploading file {currUploadNumber}/{files.length}
                    </LoadingText>
                  </LoadingContainer>
                )}
                {error && <ErrorMessage />}
                <FlexEndContainer>
                  <SecondaryButton onClick={handleClose} disabled={isLoading}>
                    Cancel
                  </SecondaryButton>
                  <Button type="submit" loading={isLoading}>
                    Upload
                  </Button>
                </FlexEndContainer>
              </PaddedForm>
            </FormProvider>
          )}
          {display === 'success' && (
            <WhiteBGContainer padding="30px">
              <FlexColumn>
                <BoldFormTitle>{categoryUploadFilesButtonStrings.title}</BoldFormTitle>
                <SuccessMessage>
                  <BoldSpan>{numUploaded} files</BoldSpan> successfully uploaded to <BoldSpan>"{uploadedCategory}"</BoldSpan>.
                </SuccessMessage>
                <FailedBrowserUploadList files={errorFiles} />
                <FlexEndContainer>
                  <SecondaryButton onClick={handleClose}>Close</SecondaryButton>
                </FlexEndContainer>
              </FlexColumn>
            </WhiteBGContainer>
          )}
        </StyledDialogContent>
      </StyledDialog>
    </>
  )
}

CategoryUploadFilesButton.propTypes = {
  categories: PropTypes.array,
  foiId: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  onSuccess: PropTypes.func
}

export default CategoryUploadFilesButton
